import React, { useReducer } from 'react';
import { CallToAction } from '../../commonTypes/links';
import Collapsible from '../../components/Collapsible';
import { Grid, GridItem } from '../../components/Grid';
import Icon from '../../components/Icon';
import colors from '../../helpers/colors';
import { applyComponentTheme } from '../../theme';
import GridContainer from '../GridContainer';
import {
  AlertBannerCloseButton,
  AlertBannerCloseText,
  AlertBannerContainer,
  AlertBannerCta,
  AlertBannerHeading,
  ButtonWrapper,
  HeadingGridItem,
  HelpButton,
  IconWrapper,
  StyledIcon,
  StyledRichText,
} from './styles';

export type AlertBannerProps = {
  /** Determines whether the banner uses an information or emergency styled theme. */
  emergencyVariant: boolean;
  /** Determines whether the user can close the banner through a clickable X. */
  closable: boolean;
  /**
   * Determines whether the banner is collapsible, which takes precedence over
   * closable.
   */
  collapsible: boolean;
  /** If the banner is collapsible, determines whether it is collapsed by default. */
  collapsedByDefault?: boolean;
  /** Title of the banner. */
  heading: string;
  /** Optional content that appears directly below the title. */
  supportingText?: string;
  /**
   * Optional content that appears on the left side of the banner, below the
   * supporting text.
   */
  leftParagraph?: string;
  /** Optional content that appears on the right side of the banner. */
  rightParagraph?: string;
  /**
   * An optional Call to Action button that appears at the bottom of the
   * banner, under the left paragraph.
   */
  cta?: CallToAction;
  /** Icon appearing before the banner heading. */
  icon: string;
  /** Help text that appears when a banner is collapsible and is collapsed. */
  showHelpText: string;
  /** Help text that appears when a banner is collapsible and is expanded. */
  hideHelpText: string;
  /**
   * For passing in additional methods, e.g. to track when a user closes the
   * banner.
   */
  handleClose?: () => void;
  /**
   * For passing in additional methods, e.g. to track when a user collapses or
   * expands the banner.
   */
  handleCollapse?: () => void;
};

/**
 * The Alert Banner component is used to inform and communicate to users about
 * important changes. They can be displayed at the top of a landing page or
 * close to the section they are applicable to. The component can be
 * manipulated through various props to change how it appears and how a user
 * interacts with it. The original Figma design spike is linked below:
 *
 * https://www.figma.com/file/8q0Q9XzFfowbKKk8v6npJN/MT_Design-spike--Designs-and-guidance
 */
const AlertBanner: React.FC<AlertBannerProps> = ({
  emergencyVariant,
  closable,
  heading,
  supportingText,
  leftParagraph,
  rightParagraph,
  cta,
  icon,
  collapsible,
  collapsedByDefault,
  showHelpText = 'Show',
  hideHelpText = 'Hide',
  handleClose,
  handleCollapse,
}) => {
  const [showAlertBanner, toggleShowAlertBanner] = useReducer((state) => !state, true);
  const [showCollapsible, toggleShowCollapsible] = useReducer(
    (state) => !state,
    collapsible ? !collapsedByDefault : true
  );

  const alignedParagraphs = [leftParagraph, rightParagraph].filter(String);

  return showAlertBanner ? (
    <AlertBannerContainer emergencyVariant={emergencyVariant}>
      <GridContainer>
        {closable && !collapsible && (
          <ButtonWrapper>
            <AlertBannerCloseButton
              onClick={() => {
                toggleShowAlertBanner();
                handleClose?.();
              }}>
              <AlertBannerCloseText>Close</AlertBannerCloseText>
              <Icon name="close" color={colors.neutral01} size="medium" />
            </AlertBannerCloseButton>
          </ButtonWrapper>
        )}
        {collapsible && (
          <ButtonWrapper>
            <HelpButton
              onClick={() => {
                toggleShowCollapsible();
                handleCollapse?.();
              }}>
              {showCollapsible ? hideHelpText : showHelpText}
            </HelpButton>
            <StyledIcon name="arrow-down" size="small" flipY={showCollapsible} />
          </ButtonWrapper>
        )}
        <Grid alignLeft>
          <HeadingGridItem
            desktop={6}
            tabletLandscape={6}
            closable={closable}
            collapsible={collapsible}>
            {icon && (
              <IconWrapper>
                <Icon name={icon} size="large" />
              </IconWrapper>
            )}
            <AlertBannerHeading>{heading}</AlertBannerHeading>
          </HeadingGridItem>
        </Grid>
        <Collapsible isOpened={showCollapsible}>
          <Grid alignLeft>
            <GridItem desktop={6} tabletLandscape={6}>
              {supportingText && (
                <StyledRichText
                  html={supportingText}
                  emergencyVariant={emergencyVariant}
                />
              )}
            </GridItem>
          </Grid>
          {alignedParagraphs.length > 0 && (
            <Grid alignLeft>
              <GridItem desktop={6} tabletLandscape={6}>
                <StyledRichText
                  html={alignedParagraphs[0] ?? ''}
                  emergencyVariant={emergencyVariant}
                />
              </GridItem>
              {alignedParagraphs.length > 1 && (
                <GridItem desktop={6} tabletLandscape={6}>
                  <StyledRichText
                    html={alignedParagraphs[1] ?? ''}
                    emergencyVariant={emergencyVariant}
                  />
                </GridItem>
              )}
            </Grid>
          )}
          {cta && <AlertBannerCta cta={cta} emergencyVariant={emergencyVariant} />}
        </Collapsible>
      </GridContainer>
    </AlertBannerContainer>
  ) : (
    <></>
  );
};

export default applyComponentTheme(AlertBanner, 'AlertBanner');
